import { Controller } from "@hotwired/stimulus"

// Direct-to-S3 uploader for static-site forms that post to
// /app/forms/submit. On submit, each <input type="file"> is uploaded
// straight to S3 via a short-lived presigned POST minted by
// /app/forms/presign; the resulting object keys are written into a
// hidden _attachments field so the normal form submission carries them.
// The file bytes never pass through the app server.
//
// Markup:
//   <form action="/app/forms/submit" method="post"
//         enctype="multipart/form-data" data-controller="form-uploads">
//     <input type="file" name="resume">
//     <div data-form-uploads-target="error" hidden></div>
//     <button type="submit">Send</button>
//   </form>
//
// Progressive enhancement: without JS the file input is ignored and the
// text fields still submit. enctype is not required (we never post the
// bytes to our server) but is harmless.
export default class extends Controller {
  static targets = ["error"]
  static values = {
    url: { type: String, default: "/app/forms/presign" },
  }

  connect() {
    this.uploading = false
    this.onSubmit = (e) => this.handleSubmit(e)
    this.element.addEventListener("submit", this.onSubmit)
  }

  disconnect() {
    this.element.removeEventListener("submit", this.onSubmit)
  }

  // File inputs that actually have a selection. Empty optional inputs are
  // skipped silently.
  fileInputs() {
    const inputs = this.element.querySelectorAll('input[type="file"]')
    return Array.from(inputs).filter(
      (input) => input.name && input.files && input.files.length > 0,
    )
  }

  async handleSubmit(event) {
    if (this.uploading) return
    const inputs = this.fileInputs()
    if (inputs.length === 0) return // nothing to upload — let it submit

    event.preventDefault()
    this.uploading = true
    this.setBusy(true)
    this.clearError()

    // One random group id per submission so its files share a key prefix.
    const subRand = this.randId()
    const attachments = {}

    try {
      for (const input of inputs) {
        const keys = []
        for (const file of input.files) {
          keys.push(await this.uploadOne(file, subRand))
        }
        attachments[input.name] = keys
        // Don't also post the raw file part to our endpoint — only keys.
        input.disabled = true
      }

      this.setHidden("_attachments", JSON.stringify(attachments))
      // Native submit() does not re-fire the submit event.
      this.element.submit()
    } catch (err) {
      this.uploading = false
      this.setBusy(false)
      inputs.forEach((i) => (i.disabled = false))
      this.showError(err)
    }
  }

  async uploadOne(file, subRand) {
    const contentType = file.type || "application/octet-stream"

    const presignResp = await fetch(this.urlValue, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        filename: file.name,
        content_type: contentType,
        sub_rand: subRand,
      }),
    })
    if (!presignResp.ok) {
      throw new Error(this.uploadErrorMessage(file, presignResp.status))
    }
    const { url, fields, key } = await presignResp.json()

    const form = new FormData()
    Object.entries(fields).forEach(([k, v]) => form.append(k, v))
    form.append("file", file)

    const uploadResp = await fetch(url, { method: "POST", body: form })
    if (!uploadResp.ok) {
      throw new Error(this.uploadErrorMessage(file, uploadResp.status))
    }
    return key
  }

  uploadErrorMessage(file, status) {
    if (status === 415) return `"${file.name}" is not an allowed file type.`
    if (status === 413) return `"${file.name}" is too large.`
    return `Upload failed for "${file.name}". Please try again.`
  }

  setHidden(name, value) {
    let input = this.element.querySelector(
      `input[type="hidden"][name="${name}"]`,
    )
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      this.element.appendChild(input)
    }
    input.value = value
  }

  setBusy(busy) {
    const submit = this.element.querySelector('[type="submit"]')
    if (submit) submit.disabled = busy
    this.element.classList.toggle("is-uploading", busy)
  }

  clearError() {
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = ""
      this.errorTarget.hidden = true
    }
  }

  showError(err) {
    const msg = (err && err.message) || "File upload failed. Please try again."
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = msg
      this.errorTarget.hidden = false
    } else {
      window.alert(msg)
    }
  }

  randId() {
    const bytes = new Uint8Array(8)
    window.crypto.getRandomValues(bytes)
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("")
  }
}
